/**
 * RescheduleDayDialog (08/2026) — „Tag verschieben"
 * Admin wählt Tag (+ optional Trainer/Zeitbereich), sieht betroffene Termine,
 * passt Grund + Nachrichten an und sendet E-Mail/WhatsApp mit persönlichem
 * Umbuchungs-Link an alle betroffenen Kunden.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CalendarX, Loader2, Send, Mail, MessageCircle } from 'lucide-react';
import { toLocalYMD } from './calendarUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RescheduleDayDialog = ({ open, onClose, currentDate, fetchAppointments }) => {
  const [date, setDate] = useState('');
  const [trainerId, setTrainerId] = useState('all');
  const [trainers, setTrainers] = useState([]);
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [reason, setReason] = useState('Ferien');
  const [channels, setChannels] = useState({ email: true, whatsapp: true });
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [whatsappBody, setWhatsappBody] = useState('');
  const [waTemplate, setWaTemplate] = useState({ approved: false, body: null, name: '' });
  const [affected, setAffected] = useState(null);
  const [statusInfo, setStatusInfo] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  useEffect(() => {
    if (open) {
      setDate(toLocalYMD(currentDate || new Date()));
      setResult(null);
      setAffected(null);
      axios.get(`${API}/users?role=trainer`, authHeaders)
        .then((r) => setTrainers(Array.isArray(r.data) ? r.data : r.data?.users || []))
        .catch(() => {});
    }
  }, [open]);

  const loadPreview = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/reschedule-day/preview`, {
        date,
        trainer_id: trainerId === 'all' ? null : trainerId,
        time_from: timeFrom || null,
        time_to: timeTo || null,
      }, authHeaders);
      setAffected(res.data.affected);
      if (!emailSubject) setEmailSubject(res.data.default_email_subject);
      if (!emailBody) setEmailBody(res.data.default_email_body);
      if (!whatsappBody) setWhatsappBody(res.data.default_whatsapp_body);
      setWaTemplate({
        approved: !!res.data.whatsapp_template_approved,
        body: res.data.whatsapp_template_body,
        name: res.data.whatsapp_template_name,
      });
      try {
        const st = await axios.post(`${API}/admin/reschedule-day/status`, { date }, authHeaders);
        setStatusInfo(st.data.requests?.length ? st.data : null);
      } catch { setStatusInfo(null); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [date, trainerId, timeFrom, timeTo]);

  useEffect(() => {
    if (open && date) loadPreview();
  }, [open, date, trainerId, timeFrom, timeTo]);

  const handleSend = async () => {
    if (!affected?.length) return;
    if (!window.confirm(`${affected.length} Kunden benachrichtigen und Termine als "Verschiebung angefragt" markieren?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/reschedule-day/send`, {
        date,
        trainer_id: trainerId === 'all' ? null : trainerId,
        time_from: timeFrom || null,
        time_to: timeTo || null,
        reason,
        channels: Object.keys(channels).filter((k) => channels[k]),
      }, authHeaders);
      setResult(res.data);
      toast.success(`${res.data.sent_email} E-Mails + ${res.data.sent_whatsapp} WhatsApp gesendet`);
      fetchAppointments?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const handleRemind = async (tokens) => {
    setReminding(true);
    try {
      const res = await axios.post(`${API}/admin/reschedule-day/remind`, { tokens }, authHeaders);
      toast.success(`${res.data.sent} Erinnerung(en) gesendet`);
      loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erinnern');
    } finally {
      setReminding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !sending && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reschedule-day-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarX className="w-5 h-5 text-orange-600" /> Tag verschieben
          </DialogTitle>
          <DialogDescription>
            Alle betroffenen Kunden erhalten einen persönlichen Link, um ihren Termin selbst umzubuchen.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 py-2" data-testid="reschedule-day-result">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
              <p className="font-semibold">✅ Versand abgeschlossen</p>
              <p className="text-sm mt-1">
                {result.affected} Termine markiert · {result.sent_email} E-Mails · {result.sent_whatsapp} WhatsApp
                {result.failed > 0 && <span className="text-red-600"> · {result.failed} Fehler</span>}
              </p>
            </div>
            <div className="max-h-40 overflow-y-auto text-sm space-y-1">
              {result.details?.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="flex-1">{d.customer}</span>
                  <Mail size={14} className={d.email ? 'text-green-600' : 'text-gray-300'} />
                  <MessageCircle size={14} className={d.whatsapp ? 'text-green-600' : 'text-gray-300'} />
                </div>
              ))}
            </div>
            <Button onClick={onClose} className="w-full" data-testid="reschedule-day-close-btn">Schliessen</Button>
          </div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Datum</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="reschedule-day-date" />
              </div>
              <div>
                <Label>Trainer</Label>
                <Select value={trainerId} onValueChange={setTrainerId}>
                  <SelectTrigger data-testid="reschedule-day-trainer"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Trainer</SelectItem>
                    {trainers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Von (optional)</Label>
                <Input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)} data-testid="reschedule-day-from" />
              </div>
              <div>
                <Label>Bis (optional)</Label>
                <Input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)} data-testid="reschedule-day-to" />
              </div>
            </div>

            <div className={`rounded-lg border p-3 text-sm ${affected?.length ? 'bg-orange-50 border-orange-200 text-orange-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`} data-testid="reschedule-day-affected">
              {loading ? (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lade betroffene Termine…</span>
              ) : affected === null ? 'Wähle ein Datum' : affected.length === 0 ? 'Keine betroffenen Termine an diesem Tag.' : (
                <>
                  <p className="font-semibold mb-1">{affected.length} betroffene Termine:</p>
                  <div className="max-h-32 overflow-y-auto space-y-0.5">
                    {affected.map((a, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="font-mono text-xs">{a.time}</span>
                        <span className="flex-1 truncate">{a.customer_name} — {a.service_name}</span>
                        {a.already_requested && <span className="text-[10px] bg-orange-200 rounded px-1">bereits angefragt</span>}
                        {!a.email && !a.phone && <span className="text-[10px] bg-red-200 text-red-700 rounded px-1">keine Kontaktdaten</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div>
              <Label>Grund (erscheint in der Nachricht)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z.B. Ferien, Krankheit" data-testid="reschedule-day-reason" />
            </div>

            {statusInfo && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm" data-testid="reschedule-day-status">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-blue-900">
                    Umbuchungs-Status: {statusInfo.done} ✓ umgebucht · {statusInfo.pending} offen
                  </p>
                  {statusInfo.pending > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={reminding}
                      onClick={() => handleRemind(statusInfo.requests.filter((r) => r.status === 'pending').map((r) => r.token))}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100 h-7 text-xs"
                      data-testid="reschedule-day-remind-all-btn"
                    >
                      {reminding ? <Loader2 className="w-3 h-3 animate-spin" /> : '🔔 Alle offenen erinnern'}
                    </Button>
                  )}
                </div>
                <div className="max-h-36 overflow-y-auto space-y-1">
                  {statusInfo.requests.map((r) => (
                    <div key={r.token} className="flex items-center gap-2">
                      <span className="font-mono text-xs">{r.time}</span>
                      <span className="flex-1 truncate">{r.customer_name} — {r.service_name}</span>
                      {r.status === 'done' ? (
                        <span className="text-[11px] bg-green-100 text-green-700 rounded px-1.5 py-0.5">✓ neu: {r.new_date} {r.new_time}</span>
                      ) : (
                        <>
                          {r.reminded_at && <span className="text-[10px] text-gray-500">erinnert</span>}
                          <button
                            onClick={() => handleRemind([r.token])}
                            disabled={reminding}
                            className="text-[11px] bg-orange-100 text-orange-700 rounded px-1.5 py-0.5 hover:bg-orange-200"
                            data-testid={`reschedule-day-remind-${r.token}`}
                          >
                            🔔 Erinnern
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={channels.email} onCheckedChange={(v) => setChannels((c) => ({ ...c, email: !!v }))} data-testid="reschedule-day-channel-email" />
                <Mail size={16} /> E-Mail
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={channels.whatsapp} onCheckedChange={(v) => setChannels((c) => ({ ...c, whatsapp: !!v }))} data-testid="reschedule-day-channel-whatsapp" />
                <MessageCircle size={16} /> WhatsApp
              </label>
            </div>

            {channels.email && (
              <div className="space-y-2">
                <Label>E-Mail</Label>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="reschedule-day-email-template-info">
                  <p className="text-xs font-semibold text-blue-800 mb-1">
                    📧 Vorlage „Tag-Verschiebung (Umbuchungslink)" wird verwendet
                  </p>
                  <p className="text-sm font-medium text-blue-900">{emailSubject}</p>
                  <p className="text-sm text-blue-900 whitespace-pre-wrap mt-1">{emailBody}</p>
                  <p className="text-[11px] text-blue-700 mt-1">
                    Text ändern unter <span className="font-medium">Kommunikation → E-Mail-Vorlagen</span> — wie bei allen E-Mails.
                  </p>
                </div>
              </div>
            )}
            {channels.whatsapp && (
              <div className="space-y-2">
                <Label>WhatsApp-Nachricht</Label>
                {waTemplate.approved ? (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3" data-testid="reschedule-day-wa-template-info">
                    <p className="text-xs font-semibold text-green-800 mb-1">
                      ✅ Von Meta bestätigtes Template „{waTemplate.name}" wird verwendet (Zustellung garantiert, auch ausserhalb des 24h-Fensters)
                    </p>
                    <p className="text-sm text-green-900 whitespace-pre-wrap">{waTemplate.body}</p>
                    <p className="text-[11px] text-green-700 mt-1">Der Text ist von Meta fixiert und kann hier nicht geändert werden. Verwaltung unter Kommunikation → WhatsApp Templates.</p>
                  </div>
                ) : (
                  <>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" data-testid="reschedule-day-wa-warning">
                      ⏳ Meta-Template „{waTemplate.name}" ist noch nicht freigegeben — die Nachricht unten kann nur Kunden zugestellt werden, die in den letzten 24h auf WhatsApp geschrieben haben. Nach der Meta-Freigabe wird automatisch das bestätigte Template verwendet.
                    </div>
                    <Textarea rows={4} value={whatsappBody} onChange={(e) => setWhatsappBody(e.target.value)} data-testid="reschedule-day-whatsapp-body" />
                  </>
                )}
              </div>
            )}

            <Button
              onClick={handleSend}
              disabled={sending || !affected?.length || (!channels.email && !channels.whatsapp)}
              className="w-full bg-orange-600 hover:bg-orange-700"
              data-testid="reschedule-day-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {affected?.length ? `${affected.length} Kunden benachrichtigen` : 'Benachrichtigen'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleDayDialog;
